// frontend/src/admin/pages/AdminReportsPage.jsx
import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from "recharts";

import {
  getFinancialReport,
  getUsageReport,
  getFeatureAdoption,
  getSystemReport,
  getAiHealth,
  getAiSpend,
} from "../api";

const TABS = ["Financial", "Usage", "System", "AI / LLM"];
const AI_REFRESH_MS = 60_000;
const PIE_COLORS = ["#6366f1", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6"];

/** Subscription revenue figures — QRFlow sells in EUR only. */
function fmtEur(n) {
  return (Number(n) || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  });
}

function Card({ label, value, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">
        {label}
      </div>
      <div className={`text-2xl font-bold ${accent ?? "text-slate-900"}`}>
        {value}
      </div>
    </div>
  );
}
Card.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  accent: PropTypes.string,
};
Card.defaultProps = { accent: undefined };

function ChartBox({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <h3 className="text-sm font-semibold text-slate-700 mb-3">{title}</h3>
      {children}
    </div>
  );
}
ChartBox.propTypes = { title: PropTypes.string.isRequired, children: PropTypes.node };
ChartBox.defaultProps = { children: null };

// ── Financial tab ──────────────────────────────────────────────────────────
function FinancialTab({ data }) {
  const statusPie = [
    {
      name: "Active",
      value: (data.mrr_by_plan ?? []).reduce(
        (s, p) => s + (p.active_subscriptions ?? 0),
        0,
      ),
    },
    { name: "Past due", value: data.past_due_count ?? 0 },
    { name: "Canceled (mo)", value: data.canceled_subscriptions_this_month ?? 0 },
  ].filter((s) => s.value > 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card label="Revenue at Risk" value={fmtEur(data.revenue_at_risk_usd)} accent="text-amber-600" />
        <Card label="Voucher Discounts (mo)" value={fmtEur(data.voucher_discount_total_usd)} />
        <Card label="Past-due Count" value={data.past_due_count ?? 0} accent="text-red-600" />
        <Card label="White Accounts" value={data.white_account_count ?? 0} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartBox title="MRR by Plan">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.mrr_by_plan ?? []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="plan" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip formatter={(v) => fmtEur(v)} />
              <Bar dataKey="mrr" fill="#6366f1" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartBox>

        <ChartBox title="Subscription Status">
          {statusPie.length === 0 ? (
            <p className="text-sm text-slate-400">No subscriptions.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={statusPie} dataKey="value" nameKey="name" outerRadius={80} label>
                  {statusPie.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartBox>
      </div>
    </div>
  );
}
FinancialTab.propTypes = { data: PropTypes.object.isRequired };

// ── Usage tab ──────────────────────────────────────────────────────────────
function UsageTab({ data, adoption }) {
  const planPie = (data.plan_adoption ?? []).map((p) => ({
    name: p.plan,
    value: p.orgs,
  }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartBox title="Top Orgs by Scans (this month)">
          {(data.top_orgs_by_scans ?? []).length === 0 ? (
            <p className="text-sm text-slate-400">No scans yet.</p>
          ) : (
            <ol className="space-y-1">
              {data.top_orgs_by_scans.slice(0, 10).map((o, i) => (
                <li key={o.org_id} className="flex justify-between text-sm">
                  <span className="text-slate-700">
                    {i + 1}. {o.org_name}
                  </span>
                  <span className="font-medium text-slate-900">{o.scans}</span>
                </li>
              ))}
            </ol>
          )}
        </ChartBox>

        <ChartBox title="Plan Adoption">
          {planPie.length === 0 ? (
            <p className="text-sm text-slate-400">No data.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={planPie} dataKey="value" nameKey="name" outerRadius={80} label>
                  {planPie.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartBox>
      </div>

      <ChartBox title="Feature Adoption (last 30 days)">
        <table className="w-full text-sm">
          <thead className="text-slate-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left py-1.5">Feature</th>
              <th className="text-right py-1.5">Eligible</th>
              <th className="text-right py-1.5">Used</th>
              <th className="text-right py-1.5">Rate</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(adoption?.features ?? []).map((f) => (
              <tr key={f.feature}>
                <td className="py-1.5 text-slate-700">{f.feature}</td>
                <td className="py-1.5 text-right">{f.eligible_orgs}</td>
                <td className="py-1.5 text-right">{f.used}</td>
                <td className="py-1.5 text-right">
                  {Math.round((f.adoption_rate ?? 0) * 100)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ChartBox>
    </div>
  );
}
UsageTab.propTypes = { data: PropTypes.object.isRequired, adoption: PropTypes.object };
UsageTab.defaultProps = { adoption: null };

// ── System tab ─────────────────────────────────────────────────────────────
function SystemTab({ data }) {
  const queueWarn = (data.celery_queue_depth ?? 0) > 100;
  const pool = data.db_pool ?? {};
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Card
          label="Celery Queue Depth"
          value={data.celery_queue_depth ?? 0}
          accent={queueWarn ? "text-red-600" : "text-slate-900"}
        />
        <Card label="Recent Errors (1h)" value={data.recent_errors ?? 0} accent={(data.recent_errors ?? 0) > 0 ? "text-red-600" : "text-slate-900"} />
        <Card
          label="DB Pool (out / size)"
          value={`${pool.checked_out ?? 0} / ${pool.size ?? 0}`}
        />
      </div>
      {queueWarn && (
        <p className="text-sm text-red-600">
          ⚠ Celery queue depth is high ({data.celery_queue_depth}). Workers may
          be falling behind.
        </p>
      )}
      <a
        href="http://localhost:5555"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-block text-sm font-medium text-indigo-600 hover:text-indigo-500"
      >
        Open Flower (Celery monitor) →
      </a>
    </div>
  );
}
SystemTab.propTypes = { data: PropTypes.object.isRequired };

// ── AI / LLM tab ─────────────────────────────────────────────────────────────
function fmtUsdCents(n) {
  return (Number(n) || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// up → green, down → red, anything else (not_configured) → amber.
function StatusDot({ status }) {
  const color =
    status === "up"
      ? "bg-green-500"
      : status === "down"
        ? "bg-red-500"
        : "bg-amber-500";
  return (
    <span
      role="status"
      aria-label={status}
      title={status}
      className={`inline-block w-2.5 h-2.5 rounded-full ${color}`}
    />
  );
}
StatusDot.propTypes = { status: PropTypes.string.isRequired };

const OVERALL_ACCENT = {
  ok: "text-green-600",
  degraded: "text-amber-600",
  down: "text-red-600",
};

function AiTab() {
  const [health, setHealth] = useState(null);
  const [spend, setSpend] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [h, s] = await Promise.all([getAiHealth(), getAiSpend()]);
      setHealth(h);
      setSpend(s);
    } catch {
      setError("Failed to load AI status.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch + auto-refresh every 60s while the tab is mounted.
  useEffect(() => {
    refresh();
    const id = setInterval(refresh, AI_REFRESH_MS);
    return () => clearInterval(id);
  }, [refresh]);

  const models = health?.models ?? [];
  const byModel = spend?.by_model ?? [];

  return (
    <div className="space-y-4">
      {/* Section 1 — Provider status */}
      <ChartBox title="Provider Status">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm">
            Gateway:{" "}
            <span
              className={`font-semibold ${
                OVERALL_ACCENT[health?.status] ?? "text-slate-500"
              }`}
            >
              {health?.status ? health.status.toUpperCase() : "—"}
            </span>
          </div>
          <button
            type="button"
            onClick={refresh}
            disabled={loading}
            className="px-3 py-1 text-sm font-medium rounded-md border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        {error ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : models.length === 0 ? (
          <p className="text-sm text-slate-400">No models reported.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left py-1.5">Model</th>
                <th className="text-left py-1.5">Status</th>
                <th className="text-right py-1.5">Latency</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {models.map((m, i) => (
                <tr key={`${m.name}-${i}`}>
                  <td className="py-1.5 text-slate-700 font-mono text-xs">
                    {m.name}
                  </td>
                  <td className="py-1.5">
                    <span className="inline-flex items-center gap-2">
                      <StatusDot status={m.status} />
                      <span className="text-slate-600">{m.status}</span>
                    </span>
                  </td>
                  <td className="py-1.5 text-right text-slate-600">
                    {m.latency_ms == null ? "—" : `${Math.round(m.latency_ms)} ms`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </ChartBox>

      {/* Section 2 — Spend this month */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <Card label="Spend This Month" value={fmtUsdCents(spend?.this_month_usd)} />
      </div>
      <ChartBox title="Spend by Model">
        {byModel.length === 0 ? (
          <p className="text-sm text-slate-400">
            No spend recorded this month. Local models are free; cloud spend
            appears here once cloud providers are used.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left py-1.5">Model</th>
                <th className="text-right py-1.5">Tokens</th>
                <th className="text-right py-1.5">Cost</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {byModel.map((m) => (
                <tr key={m.model}>
                  <td className="py-1.5 text-slate-700 font-mono text-xs">
                    {m.model}
                  </td>
                  <td className="py-1.5 text-right">
                    {(m.tokens ?? 0).toLocaleString("en-US")}
                  </td>
                  <td className="py-1.5 text-right">{fmtUsdCents(m.cost_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </ChartBox>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────
function AdminReportsPage() {
  const [tab, setTab] = useState("Financial");
  const [financial, setFinancial] = useState(null);
  const [usage, setUsage] = useState(null);
  const [adoption, setAdoption] = useState(null);
  const [system, setSystem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (which) => {
    // The AI / LLM tab is self-fetching (own data + 60s auto-refresh).
    if (which === "AI / LLM") return;
    setLoading(true);
    setError(null);
    try {
      if (which === "Financial") {
        setFinancial(await getFinancialReport());
      } else if (which === "Usage") {
        const [u, a] = await Promise.all([
          getUsageReport(),
          getFeatureAdoption(),
        ]);
        setUsage(u);
        setAdoption(a);
      } else {
        setSystem(await getSystemReport());
      }
    } catch {
      setError("Failed to load report.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(tab);
  }, [tab, load]);

  return (
    <div>
      <h1 className="text-xl font-semibold text-slate-900 mb-4">Reports</h1>

      <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden mb-5">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            aria-pressed={tab === t}
            className={`px-4 py-1.5 text-sm font-medium transition-colors ${
              tab === t
                ? "bg-indigo-600 text-white"
                : "bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "AI / LLM" ? (
        <AiTab />
      ) : loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : tab === "Financial" && financial ? (
        <FinancialTab data={financial} />
      ) : tab === "Usage" && usage ? (
        <UsageTab data={usage} adoption={adoption} />
      ) : tab === "System" && system ? (
        <SystemTab data={system} />
      ) : null}
    </div>
  );
}

export default AdminReportsPage;
